import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  CodeBracketIcon,
  MagnifyingGlassIcon,
  CogIcon,
  ServerIcon,
  CircleStackIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import AnimatedBackground from './AnimatedBackground';

const Services = () => {
  const services = [
    {
      icon: CodeBracketIcon,
      title: 'Web Development',
      description: 'Custom websites and web applications built with modern technologies. From responsive design to complex web systems, we deliver solutions that perform.',
      features: ['Responsive Design', 'Modern Frameworks', 'Performance Optimization'],
      gradient: 'from-green-500 to-green-600',
      bgGradient: 'from-green-50 to-green-100',
      link: '/services/web-development',
    },
    {
      icon: MagnifyingGlassIcon,
      title: 'SEO Optimization',
      description: 'Boost your online visibility and drive organic traffic with our comprehensive SEO strategies and digital marketing solutions.',
      features: ['Keyword Research', 'On-Page Optimization', 'Performance Tracking'],
      gradient: 'from-green-400 to-green-500',
      bgGradient: 'from-green-50 to-green-100',
      link: '/services/seo-optimization',
    },
    {
      icon: CogIcon,
      title: 'IT Consulting',
      description: 'Strategic IT guidance to help your business leverage technology effectively and make informed decisions about your digital infrastructure.',
      features: ['System Analysis', 'Technology Planning', 'Process Optimization'],
      gradient: 'from-green-600 to-green-700',
      bgGradient: 'from-green-50 to-green-100',
      link: '/services/it-consulting',
    },
    {
      icon: ServerIcon,
      title: 'System Management',
      description: 'Complete management of computer systems and IT infrastructure to ensure optimal performance and security for your business operations.',
      features: ['Server Management', 'Security Monitoring', 'Backup Solutions'],
      gradient: 'from-green-500 to-green-600',
      bgGradient: 'from-green-50 to-green-100',
      link: '/services/system-management',
    },
    {
      icon: CircleStackIcon,
      title: 'Data Processing',
      description: 'Advanced data processing and web hosting solutions to manage your business information efficiently and securely.',
      features: ['Data Analytics', 'Cloud Hosting', 'Database Optimization'],
      gradient: 'from-green-400 to-green-600',
      bgGradient: 'from-green-50 to-green-100',
      link: '/services/data-processing',
    },
    {
      icon: WrenchScrewdriverIcon,
      title: 'Custom Software',
      description: 'Bespoke software solutions and applications designed specifically for your business requirements and workflows.',
      features: ['Custom Applications', 'Software Integration', 'Maintenance Support'],
      gradient: 'from-green-600 to-green-500',
      bgGradient: 'from-green-50 to-green-100',
      link: '/services/custom-software',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section id="services" className="py-24 bg-gray-900 relative overflow-hidden">
      {/* Animated Background */}
      <AnimatedBackground />
      
      {/* Gradient Elements */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-primary-900/10 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-1/3 h-full bg-gradient-to-r from-accent-900/10 to-transparent"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Comprehensive{' '}
            <span className="bg-gradient-to-r from-green-400 to-green-500 bg-clip-text text-transparent">
              IT Solutions
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Tailored to your business needs and designed to drive growth
          </p>
        </motion.div>

        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
        >
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <motion.div
                key={index}
                className="group relative h-full"
                variants={cardVariants}
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-10 rounded-2xl transform rotate-1 group-hover:rotate-2 transition-transform duration-300`}></div>
                <div className="relative bg-gray-800 rounded-2xl p-8 shadow-lg group-hover:shadow-2xl transition-all duration-300 border border-gray-700 group-hover:border-primary-500 h-full flex flex-col">
                  {/* Icon */}
                  <div className="relative mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-br ${service.gradient} rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300`}>
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    
                    {/* Floating dot */}
                    <div className={`absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-br ${service.gradient} rounded-full opacity-70 group-hover:opacity-100 transition-opacity duration-300`}></div>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-primary-300 transition-colors duration-300">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-300 mb-6 leading-relaxed flex-grow">
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, featureIndex) => (
                      <li
                        key={featureIndex}
                        className="flex items-center text-sm text-gray-400"
                      >
                        <div className={`w-2 h-2 bg-gradient-to-br ${service.gradient} rounded-full mr-3 flex-shrink-0`}></div>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Link to={service.link}>
                    <motion.button
                      className={`w-full bg-gradient-to-r ${service.gradient} text-white py-3 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Learn More
                    </motion.button>
                  </Link>

                  {/* Decorative Elements */}
                  <div className="absolute top-4 right-4 w-2 h-2 bg-primary-200 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-4 left-4 w-1 h-1 bg-accent-300 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ animationDelay: '0.1s' }}></div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* View All Services Button */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <Link to="/services">
            <motion.button
              className="bg-gradient-to-r from-green-600 to-green-500 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-2xl hover:shadow-green-500/25 transition-all duration-300"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              View All Services
              <svg className="w-5 h-5 ml-2 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </motion.button>
          </Link>
        </motion.div>

      </div>
    </section>
  );
};

export default Services;
